import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { AuthCore } from '../core/AuthCore';
import toast from 'react-hot-toast';
import { maskName } from '../utils/maskName';
import { 
  Users as UserGroupIcon, 
  DollarSign as CurrencyDollarIcon, 
  BarChart3 as ChartBarIcon, 
  Copy as ClipboardDocumentIcon,
  CheckCircle as CheckCircleIcon,
  Clock as ClockIcon,
  XCircle as XCircleIcon,
  X as XIcon,
  Mail as MailIcon,
  Edit as EditIcon
} from 'lucide-react';

// Development-only logging
const isDev = import.meta.env.DEV;
const devLog = (...args: any[]) => {
  if (isDev) console.log(...args);
};
const devError = (...args: any[]) => {
  if (isDev) console.error(...args);
};

// Mask PayPal Order ID: show first 4 and last 3 characters
const maskOrderId = (orderId: string) => {
  if (!orderId || orderId.length < 8) return orderId;
  const first = orderId.substring(0, 4);
  const last = orderId.substring(orderId.length - 3);
  const masked = '*'.repeat(Math.min(orderId.length - 7, 5));
  return `${first}${masked}${last}`;
};

const AffiliatePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [affiliateStatus, setAffiliateStatus] = useState<any>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  
  // Application form state
  const [applicationForm, setApplicationForm] = useState({
    reason: '',
    paypal_email: '',
    // Honeypot fields (should remain empty - bots will fill them)
    website: '', // Hidden field - bot trap
    company_name: '' // Hidden field - bot trap
  });
  const [submitting, setSubmitting] = useState(false);
  const [formLoadTime] = useState(Date.now()); // Track time on page for bot detection

  // Commission detail modal state
  const [selectedReferral, setSelectedReferral] = useState<any>(null);
  const [commissionDetails, setCommissionDetails] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Pagination state
  const [showAllReferrals, setShowAllReferrals] = useState(false);
  const [showAllPayouts, setShowAllPayouts] = useState(false);

  // PayPal email update state
  const [showPayPalModal, setShowPayPalModal] = useState(false);
  const [paypalStep, setPaypalStep] = useState<'request' | 'verify'>('request');
  const [newPaypalEmail, setNewPaypalEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  // Payout request confirmation state
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState(0);
  const [payoutLoading, setPayoutLoading] = useState(false);

  useEffect(() => {
    fetchAffiliateStatus();
  }, []);

  const fetchAffiliateStatus = async () => {
    try {
      setLoading(true);
      
      const token = await AuthCore.getInstance().getAccessToken();
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'https://api.metapic.app';
      const endpoint = `${apiUrl}/api/v1/affiliate/status`;
      
      devLog('Fetching affiliate status...', {
        apiUrl,
        endpoint,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
      });
      
      if (!token) {
        devError('No authentication token available');
        toast.error('Please login to access affiliate features');
        setLoading(false);
        return;
      }
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Check if response is OK before parsing
      if (!response.ok) {
        devError('Affiliate status fetch failed:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url
        });
        
        // Try to get error message
        const text = await response.text();
        devError('Response body:', text.substring(0, 200));
        
        toast.error(`Failed to load affiliate status (${response.status})`);
        setLoading(false);
        return;
      }

      const data = await response.json();
      
      devLog('Affiliate status response:', {
        status: response.status,
        success: data.success,
        data
      });
      
      if (data.success) {
        setAffiliateStatus(data.data);
        
        // If approved, fetch dashboard data
        if (data.data.is_affiliate && data.data.status === 'approved') {
          fetchDashboard();
        }
      } else {
        devError('Affiliate status fetch failed:', data);
      }
    } catch (error) {
      devError('Error fetching affiliate status:', error);
      toast.error('Failed to load affiliate status');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const token = await AuthCore.getInstance().getAccessToken();
      
      if (!token) {
        devError('No authentication token for dashboard');
        return;
      }
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'https://api.metapic.app';
      const response = await fetch(`${apiUrl}/api/v1/affiliate/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setDashboardData(data.data);
      }
    } catch (error) {
      devError('Error fetching dashboard:', error);
    }
  };

  const handleSubmitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (applicationForm.reason.length < 50) {
      toast.error('Please provide a detailed reason (minimum 50 characters)');
      return;
    }

    if (applicationForm.reason.length > 500) {
      toast.error('Reason is too long (maximum 500 characters)');
      return;
    }

    try {
      setSubmitting(true);
      
      const token = await AuthCore.getInstance().getAccessToken();
      const apiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'https://api.metapic.app';
      
      // Calculate time on page (anti-bot measure)
      const timeOnPage = Math.floor((Date.now() - formLoadTime) / 1000); // seconds
      
      devLog('Submitting affiliate application...', {
        url: `${apiUrl}/api/v1/affiliate/apply`,
        hasToken: !!token,
        formData: {
          reasonLength: applicationForm.reason.length,
          paypalEmail: applicationForm.paypal_email,
          timeOnPage
        }
      });
      
      if (!token) {
        toast.error('Please login to submit application');
        return;
      }
      
      // Prepare form data with anti-spam measures
      const formData = {
        reason: applicationForm.reason,
        paypal_email: applicationForm.paypal_email,
        // Honeypot fields (should be empty)
        website: applicationForm.website,
        company_name: applicationForm.company_name,
        // Time on page (bot detection)
        _timeOnPage: timeOnPage
      };
      
      const response = await fetch(`${apiUrl}/api/v1/affiliate/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      devLog('Affiliate application response:', {
        status: response.status,
        success: data.success,
        data
      });
      
      if (data.success) {
        toast.success('Application submitted successfully!');
        fetchAffiliateStatus(); // Refresh status
        setApplicationForm({ 
          reason: '', 
          paypal_email: '',
          website: '',
          company_name: ''
        }); // Reset form
      } else {
        const errorMessage = data.error?.message || data.message || 'Failed to submit application';
        devError('Application failed:', data);
        toast.error(errorMessage);
      }
    } catch (error) {
      devError('Error submitting application:', error);
      toast.error('Network error: Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const copyReferralLink = () => {
    const referralLink = `${window.location.origin}/?ref=${dashboardData?.affiliate_code}`;
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const fetchCommissionDetails = async (referredUserId: string) => {
    try {
      setLoadingDetails(true);
      const token = await AuthCore.getInstance().getAccessToken();
      
      if (!token) {
        toast.error('Please login to view details');
        return;
      }
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'https://api.metapic.app';
      const response = await fetch(
        `${apiUrl}/api/v1/affiliate/commissions?referred_user_id=${referredUserId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setCommissionDetails(data.data.commissions || []);
      } else {
        toast.error('Failed to load commission details');
      }
    } catch (error) {
      devError('Error fetching commission details:', error);
      toast.error('Failed to load commission details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const openCommissionModal = (referral: any) => {
    setSelectedReferral(referral);
    fetchCommissionDetails(referral.referred_user_id);
  };

  const closeCommissionModal = () => {
    setSelectedReferral(null);
    setCommissionDetails([]);
  };

  const openPayoutModal = () => {
    if (!dashboardData?.stats?.approved_earnings || dashboardData.stats.approved_earnings < 20) {
      toast.error('Minimum payout amount is $20');
      return;
    }
    setPayoutAmount(dashboardData.stats.approved_earnings);
    setShowPayoutModal(true);
  };

  const closePayoutModal = () => {
    setShowPayoutModal(false);
    setPayoutAmount(0);
  };

  const requestPayout = async () => {
    if (payoutAmount < 20) {
      toast.error('Minimum payout amount is $20');
      return;
    }

    try {
      setPayoutLoading(true);
      const token = await AuthCore.getInstance().getAccessToken();
      
      if (!token) {
        toast.error('Please login to request payout');
        return;
      }
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'https://api.metapic.app';
      const response = await fetch(`${apiUrl}/api/v1/affiliate/request-payout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: payoutAmount
          // PayPal email will be fetched from affiliate_applications by backend
        })
      });

      const data = await response.json();
      
      devLog('Payout request response:', { status: response.status, data });
      
      if (data.success) {
        toast.success('Payout request submitted! We will process it within 2-3 business days.');
        closePayoutModal();
        fetchDashboard(); // Refresh data
      } else {
        devError('Payout request failed:', data);
        toast.error(data.error?.message || 'Failed to create payout request');
      }
    } catch (error) {
      devError('Error requesting payout:', error);
      toast.error('Failed to create payout request');
    } finally {
      setPayoutLoading(false);
    }
  };

  const openPayPalModal = () => {
    setShowPayPalModal(true);
    setPaypalStep('request');
    setNewPaypalEmail('');
    setOtpCode('');
    setMaskedEmail('');
  };

  const closePayPalModal = () => {
    setShowPayPalModal(false);
    setPaypalStep('request');
    setNewPaypalEmail('');
    setOtpCode('');
    setMaskedEmail('');
  };

  const requestOTP = async () => {
    if (!newPaypalEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newPaypalEmail)) {
      toast.error('Please enter a valid PayPal email address');
      return;
    }

    try {
      setOtpLoading(true);
      const token = await AuthCore.getInstance().getAccessToken();
      
      if (!token) {
        toast.error('Please login to continue');
        return;
      }
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'https://api.metapic.app';
      const response = await fetch(`${apiUrl}/api/v1/affiliate/paypal-email/request-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setMaskedEmail(data.data.masked_email);
        setPaypalStep('verify');
        toast.success(`Verification code sent to ${data.data.masked_email}. Please check spam/junk folder if you don't see it.`, {
          duration: 6000
        });
      } else {
        toast.error(data.error?.message || 'Failed to send verification code');
      }
    } catch (error) {
      devError('Error requesting OTP:', error);
      toast.error('Failed to send verification code');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyAndUpdatePayPal = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Please enter the 6-digit verification code');
      return;
    }

    if (!newPaypalEmail) {
      toast.error('Please enter your new PayPal email');
      return;
    }

    try {
      setOtpLoading(true);
      const token = await AuthCore.getInstance().getAccessToken();
      
      if (!token) {
        toast.error('Please login to continue');
        return;
      }
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'https://api.metapic.app';
      const response = await fetch(`${apiUrl}/api/v1/affiliate/paypal-email/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          otp_code: otpCode,
          new_paypal_email: newPaypalEmail
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('PayPal email updated successfully!');
        closePayPalModal();
        fetchAffiliateStatus(); // Refresh status to get new email
        fetchDashboard();
      } else {
        toast.error(data.error?.message || 'Failed to verify code');
      }
    } catch (error) {
      devError('Error verifying OTP:', error);
      toast.error('Failed to verify code');
    } finally {
      setOtpLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  // Show rejected status - NO FORM!
  if (affiliateStatus?.status === 'rejected') {
    const rejectionReason = affiliateStatus?.application?.rejection_reason || 'No reason provided';
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
          <div className="container mx-auto px-4 py-8 max-w-4xl">
            <div className="bg-white rounded-xl shadow-lg border-2 border-red-200 p-8 text-center">
              <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Application Rejected
              </h1>
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <p className="text-lg font-semibold text-red-800 mb-2">
                  Your affiliate application was rejected.
                </p>
                <p className="text-red-700">
                  <span className="font-medium">Reason:</span> {rejectionReason}
                </p>
              </div>
              <p className="text-gray-600 mb-4">
                If you believe this was a mistake or would like to discuss your application, please contact our support team.
              </p>
              <div className="mt-8 text-sm text-gray-400">
                Reviewed on {affiliateStatus?.application?.reviewed_at ? new Date(affiliateStatus.application.reviewed_at).toLocaleDateString() : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Show application form if not affiliate and not pending/rejected
  if (!affiliateStatus?.is_affiliate && affiliateStatus?.status !== 'pending' && affiliateStatus?.status !== 'rejected') {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
          <div className="container mx-auto px-4 py-8 max-w-4xl">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Join Our Lifetime Affiliate Program
              </h1>
              <p className="text-xl text-gray-600">
                Earn 50% on first month + 10% lifetime recurring on all future purchases
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <CurrencyDollarIcon className="h-12 w-12 text-green-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">50% First Month</h3>
                <p className="text-gray-600">Earn 50% on the first month when someone signs up</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <ChartBarIcon className="h-12 w-12 text-blue-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">10% Lifetime</h3>
                <p className="text-gray-600">Earn 10% on ALL renewals and purchases forever</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <UserGroupIcon className="h-12 w-12 text-purple-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">True Passive Income</h3>
                <p className="text-gray-600">Earn recurring commissions as long as they use Metapic</p>
              </div>
            </div>

            {/* Application Form */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Apply Now</h2>
              
              <form onSubmit={handleSubmitApplication} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Why do you want to become an affiliate? *
                  </label>
                  <textarea
                    value={applicationForm.reason}
                    onChange={(e) => setApplicationForm({ ...applicationForm, reason: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={6}
                    placeholder="Tell us about your audience, marketing channels, and why you'd be a great affiliate partner..."
                    required
                    minLength={50}
                    maxLength={500}
                  />
                  <p className={`text-sm mt-2 ${applicationForm.reason.length > 500 ? 'text-red-600' : 'text-gray-500'}`}>
                    {applicationForm.reason.length}/500 characters (min 50)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PayPal Email *
                  </label>
                  <input
                    type="email"
                    value={applicationForm.paypal_email}
                    onChange={(e) => setApplicationForm({ ...applicationForm, paypal_email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@paypal.com"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    We'll send your commission payouts to this PayPal email
                  </p>
                </div>

                {/* Honeypot fields - hidden from users but visible to bots */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    value={applicationForm.website}
                    onChange={(e) => setApplicationForm({ ...applicationForm, website: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  <input
                    type="text"
                    name="company_name"
                    value={applicationForm.company_name}
                    onChange={(e) => setApplicationForm({ ...applicationForm, company_name: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={
                    submitting || 
                    applicationForm.reason.length < 50 || 
                    applicationForm.reason.length > 500 ||
                    !applicationForm.paypal_email
                  }
                  className="w-full"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </form>
            </div>

            {/* FAQ Section */}
            <div className="mt-12 bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">How much can I earn?</h4>
                  <p className="text-gray-600">
                    You earn 50% of the first month when someone signs up, plus 10% on ALL future purchases they make forever. 
                    For example: Business Yearly ($1350) signup = $56.25 upfront. Every year they renew = $135. 
                    Plus 10% on credit top-ups. True passive income that compounds over time!
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">What counts as a "future purchase"?</h4>
                  <p className="text-gray-600">
                    You earn 10% on ALL transactions: subscription renewals (monthly/yearly), credit top-ups, 
                    plan upgrades, and any other purchases. As long as they're your referral, you earn on everything!
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">When do I get paid?</h4>
                  <p className="text-gray-600">You can request a payout once your approved balance reaches $50. Payouts are processed manually within 2-3 business days via PayPal.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">How long does approval take?</h4>
                  <p className="text-gray-600">Most applications are reviewed within 24-48 hours. We'll notify you via email once your application is approved or if we need more information.</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">How does referral tracking work?</h4>
                  <p className="text-gray-600">
                    When someone clicks your referral link, we store a tracking cookie for 30 days. They can browse, leave, and come back - 
                    as long as they sign up within 30 days, you get credit. Once they sign up, they're your referral forever for all future commissions!
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Are cookies required for tracking?</h4>
                  <p className="text-gray-600">Yes, we use cookies and local storage to track referrals. This is standard practice for affiliate programs and ensures accurate attribution. Users who block cookies may not be properly tracked, but most users have cookies enabled by default.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Show pending status
  if (affiliateStatus?.status === 'pending') {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
          <div className="container mx-auto px-4 py-8 max-w-4xl">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
              <ClockIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Application Under Review
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                Your affiliate application is currently being reviewed by our team.
              </p>
              <p className="text-gray-500">
                We typically review applications within 24-48 hours. You'll receive an email notification once your application is approved.
              </p>
              <div className="mt-8 text-sm text-gray-400">
                Applied on {new Date(affiliateStatus.applied_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Show rejected status
  if (affiliateStatus?.status === 'rejected') {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
          <div className="container mx-auto px-4 py-8 max-w-4xl">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
              <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Application Not Approved
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                Unfortunately, your affiliate application was not approved at this time.
              </p>
              <p className="text-gray-500">
                You may reapply after 30 days. If you have questions, please contact our support team.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Show affiliate dashboard if approved
  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Affiliate Dashboard</h1>
                {affiliateStatus?.status !== 'suspended' && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    <span className="text-gray-600">Active Affiliate</span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-600 font-medium capitalize">
                      {dashboardData?.tier_info?.tier || 'Basic'} Tier
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-600">
                      First Purchase: <span className="font-semibold text-green-600">{dashboardData?.tier_info?.rates?.first_payment || 51}%</span>
                      {' • '}
                      Recurring: <span className="font-semibold text-blue-600">{dashboardData?.tier_info?.rates?.recurring || 11}%</span>
                    </span>
                  </div>
                )}
              </div>

              {/* PayPal Email - Compact in Header */}
              {affiliateStatus?.status !== 'suspended' && affiliateStatus?.application?.paypal_email && (
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
                  <MailIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">{affiliateStatus.application.paypal_email}</span>
                  <button
                    onClick={openPayPalModal}
                    className="ml-2 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Update PayPal Email"
                  >
                    <EditIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* SUSPENDED WARNING BANNER - FULL PAGE */}
          {affiliateStatus?.status === 'suspended' ? (
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-4">
                <XCircleIcon className="h-10 w-10 text-red-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-red-900 mb-3">
                    ⚠️ Your Affiliate Account is Suspended
                  </h3>
                  <p className="text-lg text-red-800 mb-4">
                    Your affiliate account has been suspended. You can no longer earn commissions, 
                    and your referral links are inactive.
                  </p>
                  
                  {affiliateStatus?.suspended_reason && (
                    <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-4">
                      <p className="text-sm font-semibold text-red-900 mb-1">Reason for suspension:</p>
                      <p className="text-base text-red-800">{affiliateStatus.suspended_reason}</p>
                    </div>
                  )}

                  <div className="bg-white border-2 border-red-400 rounded-lg p-5 mb-5">
                    <p className="text-lg font-bold text-red-900 mb-2">
                      ⚠️ IMPORTANT: Any remaining balance WILL NOT BE PAID!
                    </p>
                    <p className="text-base text-gray-700">
                      Due to the suspension, any pending or approved earnings will be forfeited and will not be processed for payout.
                    </p>
                  </div>

                  <p className="text-base text-gray-700 mb-4">
                    If you believe this suspension is an error or would like to discuss this decision, please contact our support team immediately.
                  </p>

                  <a
                    href="/contact"
                    className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-lg"
                  >
                    Contact Support
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Stats Grid - ONLY SHOW IF NOT SUSPENDED */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="text-sm text-gray-500 mb-1">Total Clicks</div>
              <div className="text-3xl font-bold text-gray-900">{dashboardData?.stats?.total_clicks || 0}</div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="text-sm text-gray-500 mb-1">Conversions</div>
              <div className="text-3xl font-bold text-gray-900">{dashboardData?.stats?.total_conversions || 0}</div>
              <div className="text-sm text-green-600 mt-1">
                {dashboardData?.stats?.conversion_rate_percent?.toFixed(1) || 0}% rate
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="text-sm text-gray-500 mb-1">Pending Review</div>
              <div className="text-3xl font-bold text-yellow-600">
                ${dashboardData?.stats?.pending_earnings?.toFixed(2) || '0.00'}
              </div>
              <div className="text-xs text-gray-500 mt-1">Awaiting admin approval</div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="text-sm text-gray-500 mb-1">Total Earned</div>
              <div className="text-3xl font-bold text-green-600">
                ${dashboardData?.stats?.total_earned?.toFixed(2) || '0.00'}
              </div>
            </div>
          </div>

          {/* Referral Link & Earnings */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Referral Links */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Referral Links</h3>
              
              {/* Web App Link */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  💻 Web App Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/?ref=${dashboardData?.affiliate_code}`}
                    readOnly
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm"
                  />
                  <Button onClick={copyReferralLink} variant="outline" size="sm">
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  For users who want to use the web version
                </p>
              </div>

              {/* Mobile App Link */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  📱 Mobile App Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/app?ref=${dashboardData?.affiliate_code}`}
                    readOnly
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm"
                  />
                  <Button 
                    onClick={() => {
                      const mobileLink = `${window.location.origin}/app?ref=${dashboardData?.affiliate_code}`;
                      navigator.clipboard.writeText(mobileLink);
                      toast.success('Mobile app link copied!');
                    }} 
                    variant="outline" 
                    size="sm"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Direct link to download from Google Play
                </p>
              </div>
            </div>

            {/* Earnings Summary */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Earnings Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Pending Review:</span>
                  <span className="font-semibold text-gray-900">
                    ${dashboardData?.stats?.pending_earnings?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Available for Payout:</span>
                  <span className="font-semibold text-green-600">
                    ${dashboardData?.stats?.approved_earnings?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
              <Button
                onClick={openPayoutModal}
                disabled={(dashboardData?.stats?.approved_earnings || 0) < 20}
                className="w-full mt-4"
              >
                Request Payout (Min $20)
              </Button>
            </div>
          </div>

          {/* Recent Activity Tables */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Recent Referrals - Only Converted */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Recent Referrals</h3>
                <p className="text-xs text-gray-500 mt-1">Click to view commission breakdown</p>
              </div>
              {dashboardData?.recent_referrals && dashboardData.recent_referrals.filter((r: any) => r.status === 'converted').length > 0 ? (
                <>
                <div className="space-y-3">
                  {dashboardData.recent_referrals
                    .filter((r: any) => r.status === 'converted')
                    .slice(0, showAllReferrals ? undefined : 5)
                    .map((referral: any) => (
                      <div 
                        key={referral.id} 
                        className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors rounded px-2 -mx-2"
                        onClick={() => openCommissionModal(referral)}
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {referral.referred_user?.name 
                              ? maskName(referral.referred_user.name)
                              : referral.referred_user_email 
                                ? referral.referred_user_email.split('@')[0] + '***@' + referral.referred_user_email.split('@')[1]
                                : 'Anonymous User'
                            }
                          </div>
                          <div className="text-xs text-gray-500">
                            Purchased on {new Date(referral.conversion_date || referral.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right mr-2">
                            <div className="text-sm font-semibold text-green-600">
                              ${(referral.total_earnings || 0).toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">
                              earned
                            </div>
                          </div>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Converted
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
                {dashboardData.recent_referrals.filter((r: any) => r.status === 'converted').length > 5 && (
                  <button
                    onClick={() => setShowAllReferrals(!showAllReferrals)}
                    className="w-full mt-4 text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    {showAllReferrals ? '↑ Show Less' : `↓ Show All (${dashboardData.recent_referrals.filter((r: any) => r.status === 'converted').length} total)`}
                  </button>
                )}
                </>
              ) : (
                <p className="text-gray-500 text-center py-8">No conversions yet</p>
              )}
            </div>

            {/* Payout History - Payout Requests */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Payout History</h3>
                <p className="text-xs text-gray-500 mt-1">Your payout requests and their status</p>
              </div>
              {dashboardData?.payout_history && dashboardData.payout_history.length > 0 ? (
                <>
                <div className="space-y-3">
                  {dashboardData.payout_history
                    .slice(0, showAllPayouts ? undefined : 5)
                    .map((payout: any) => (
                      <div key={payout.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            ${payout.amount.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500">
                            To: {payout.payment_email}
                          </div>
                          <div className="text-xs text-gray-400">
                            Requested: {new Date(payout.requested_at || payout.created_at).toLocaleDateString()}
                          </div>
                          {payout.completed_at && (
                            <div className="text-xs text-gray-400">
                              Completed: {new Date(payout.completed_at).toLocaleDateString()}
                            </div>
                          )}
                          {payout.transaction_id && (
                            <div className="text-xs text-gray-400">
                              TX: {payout.transaction_id}
                            </div>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          payout.status === 'completed' 
                            ? 'bg-green-100 text-green-800'
                            : payout.status === 'processing'
                            ? 'bg-blue-100 text-blue-800'
                            : payout.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : payout.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                        </span>
                      </div>
                    ))}
                </div>
                {dashboardData.payout_history.length > 5 && (
                  <button
                    onClick={() => setShowAllPayouts(!showAllPayouts)}
                    className="w-full mt-4 text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    {showAllPayouts ? '↑ Show Less' : `↓ Show All (${dashboardData.payout_history.length} total)`}
                  </button>
                )}
                </>
              ) : (
                <p className="text-gray-500 text-center py-8">No payouts yet</p>
              )}
            </div>
          </div>
          </>
          )}
        </div>
      </div>

      {/* Commission Details Modal */}
      {selectedReferral && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold mb-1">Commission Breakdown</h3>
                  <p className="text-purple-100 text-sm">
                    {selectedReferral.referred_user?.name 
                      ? maskName(selectedReferral.referred_user.name)
                      : selectedReferral.referred_user_email 
                        ? selectedReferral.referred_user_email.split('@')[0] + '***@' + selectedReferral.referred_user_email.split('@')[1]
                        : 'Anonymous User'
                    }
                  </p>
                  <p className="text-purple-200 text-xs mt-1">
                    Total Earned: <span className="font-bold text-white">${(selectedReferral.total_earnings || 0).toFixed(2)}</span>
                  </p>
                </div>
                <button
                  onClick={closeCommissionModal}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {loadingDetails ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-gray-500 mt-4">Loading commission details...</p>
                </div>
              ) : commissionDetails.length > 0 ? (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-purple-600">
                          {commissionDetails.length}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Total Transactions</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-600">
                          ${commissionDetails.reduce((sum: number, c: any) => sum + Number(c.commission_amount), 0).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Total Earned</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-600">
                          {commissionDetails.filter((c: any) => c.status === 'paid').length}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Paid Out</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end mb-2">
                    <span className="text-xs text-gray-400">
                      Scroll for more ↓
                    </span>
                  </div>

                  <div className="space-y-3">
                  {commissionDetails.map((commission: any, index: number) => (
                    <div 
                      key={commission.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-gray-400">#{index + 1}</span>
                            <span className="text-sm font-semibold text-gray-900">
                              {commission.commission_type === 'first_payment' ? '🎉 First Purchase' : '🔄 Recurring'}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              commission.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              commission.status === 'approved' ? 'bg-green-100 text-green-800' :
                              commission.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {commission.status.charAt(0).toUpperCase() + commission.status.slice(1)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600">
                            Order Amount: <span className="font-medium">${Number(commission.order_amount).toFixed(2)}</span>
                          </div>
                          <div className="text-xs text-gray-600">
                            Commission Rate: <span className="font-medium">{Number(commission.commission_rate)}%</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(commission.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">
                            +${Number(commission.commission_amount).toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500">
                            commission
                          </div>
                        </div>
                      </div>
                      {commission.paypal_order_id && (
                        <div className="text-xs text-gray-400 mt-2 font-mono bg-gray-50 px-2 py-1 rounded">
                          Order ID: {maskOrderId(commission.paypal_order_id)}
                        </div>
                      )}
                    </div>
                  ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No commission details available</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">
                  Total Commissions: {commissionDetails.length}
                </span>
                <button
                  onClick={closeCommissionModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PayPal Email Update Modal */}
      {showPayPalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MailIcon className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="text-2xl font-bold text-white">Update PayPal Email</h3>
                    <p className="text-blue-100 text-sm mt-1">Secure verification required</p>
                  </div>
                </div>
                <button
                  onClick={closePayPalModal}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {paypalStep === 'request' ? (
                // Step 1: Enter new PayPal email
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <div className="text-blue-600 text-2xl">🔒</div>
                      <div>
                        <p className="text-sm font-semibold text-blue-900 mb-1">Security Check</p>
                        <p className="text-sm text-blue-700">
                          We'll send a 6-digit verification code to your email address to confirm this change.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current PayPal Email
                    </label>
                    <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-600">
                      {affiliateStatus?.application?.paypal_email || 'Not set'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New PayPal Email *
                    </label>
                    <input
                      type="email"
                      value={newPaypalEmail}
                      onChange={(e) => setNewPaypalEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your-new-email@paypal.com"
                      disabled={otpLoading}
                    />
                  </div>

                  <Button
                    onClick={requestOTP}
                    disabled={otpLoading || !newPaypalEmail}
                    className="w-full"
                  >
                    {otpLoading ? 'Sending...' : 'Send Verification Code'}
                  </Button>
                </div>
              ) : (
                // Step 2: Enter OTP and verify
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <div className="text-green-600 text-2xl">📧</div>
                      <div>
                        <p className="text-sm font-semibold text-green-900 mb-1">Code Sent!</p>
                        <p className="text-sm text-green-700">
                          We sent a 6-digit verification code to <strong>{maskedEmail}</strong>
                        </p>
                        <p className="text-xs text-green-600 mt-2">
                          Code expires in 10 minutes
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <div className="text-yellow-600 text-xl">⚠️</div>
                      <p className="text-xs text-yellow-800">
                        <strong>Can't find the email?</strong> Please check your <strong>spam or junk folder</strong>. Sometimes our emails end up there.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New PayPal Email
                    </label>
                    <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-600">
                      {newPaypalEmail}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification Code *
                    </label>
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
                      placeholder="000000"
                      maxLength={6}
                      disabled={otpLoading}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Enter the 6-digit code from your email
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => setPaypalStep('request')}
                      disabled={otpLoading}
                      className="flex-1 bg-gray-500 hover:bg-gray-600"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={verifyAndUpdatePayPal}
                      disabled={otpLoading || otpCode.length !== 6}
                      className="flex-1"
                    >
                      {otpLoading ? 'Verifying...' : 'Verify & Update'}
                    </Button>
                  </div>

                  <button
                    onClick={requestOTP}
                    disabled={otpLoading}
                    className="w-full text-sm text-blue-600 hover:text-blue-700 underline"
                  >
                    Didn't receive code? Resend
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payout Request Confirmation Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-blue-600 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CurrencyDollarIcon className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="text-2xl font-bold text-white">Request Payout</h3>
                    <p className="text-green-100 text-sm mt-1">Confirm withdrawal amount</p>
                  </div>
                </div>
                <button
                  onClick={closePayoutModal}
                  className="text-white hover:text-gray-200 transition-colors"
                  disabled={payoutLoading}
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payout Amount (Full Balance Only)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg font-semibold">$</span>
                  <input
                    type="number"
                    value={payoutAmount}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-lg font-semibold cursor-not-allowed"
                    disabled
                    readOnly
                  />
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  ℹ️ You must withdraw your full available balance. Partial withdrawals are not supported.
                </p>
              </div>

              {affiliateStatus?.application?.paypal_email && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-xs text-gray-600 mb-1">PayPal Email</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {affiliateStatus.application.paypal_email}
                  </p>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-yellow-800">
                  <strong>Processing Time:</strong> Payouts are processed within 2-3 business days. You'll receive an email confirmation once processed.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={closePayoutModal}
                  disabled={payoutLoading}
                  className="flex-1 bg-gray-500 hover:bg-gray-600"
                >
                  Cancel
                </Button>
                <Button
                  onClick={requestPayout}
                  disabled={payoutLoading || payoutAmount < 20}
                  className="flex-1"
                >
                  {payoutLoading ? 'Processing...' : 'Confirm Payout'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AffiliatePage;
